import {
  MapPin,
  Phone,
  Navigation,
  Clock,
  ArrowUpRight,
  Instagram,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskLine, SectionLabel } from "./Reveal";
import { BRAND, LOCATIONS } from "../data/site";
import { useBookingMode } from "../hooks/useBookingMode";

export const Locations = () => {
  const { bookProps } = useBookingMode();

  return (
  <section
    id="locations"
    data-testid="location-selector-section"
    className="relative bg-[#0d1219] py-24 lg:py-36"
  >
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
      <SectionLabel testId="locations-label">
        Chapter 06 · Two Vaults
      </SectionLabel>
      <h2 className="mt-7 max-w-3xl font-cond text-4xl font-extrabold uppercase leading-[0.92] sm:text-5xl lg:text-6xl">
        <MaskLine>Pick your shop.</MaskLine>
        <MaskLine delay={0.1} className="brass-text">
          Both are minutes away.
        </MaskLine>
      </h2>

      <div className="mt-14 grid gap-5 lg:grid-cols-2">
        {LOCATIONS.map((l, i) => (
          <Reveal key={l.id} delay={i * 0.12}>
            <article
              data-testid={`location-card-${l.id}`}
              className="group steel-panel grain relative flex h-full flex-col overflow-hidden p-7 transition-all duration-500 hover:border-[#c5a059]/45 sm:p-9"
            >
              <span className="absolute right-6 top-6 font-display text-xs text-[#c5a059]/45">
                0{i + 1}
              </span>

              <h3 className="font-cond text-3xl font-extrabold uppercase leading-none text-[#f2ece0] sm:text-4xl">
                {l.city}
              </h3>
              <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#c5a059]">
                {l.note}
              </p>

              <div className="mt-8 space-y-3 text-sm text-slate-400">
                <p className="flex items-start gap-3">
                  <MapPin size={15} className="mt-0.5 shrink-0 text-[#c5a059]" />
                  {l.address}
                </p>
                <a
                  href={`tel:${l.tel}`}
                  data-testid={`location-call-${l.id}`}
                  className="flex items-center gap-3 transition-colors hover:text-[#d8b46b]"
                >
                  <Phone size={15} className="shrink-0 text-[#c5a059]" />
                  {l.phone}
                </a>
                <p className="flex items-center gap-3">
                  <Clock size={15} className="shrink-0 text-[#c5a059]" />
                  {BRAND.hours}
                </p>
                <p className="flex items-start gap-3 text-xs text-slate-500">
                  <Navigation size={15} className="mt-0.5 shrink-0 text-[#c5a059]/70" />
                  Serving {l.serves}
                </p>
              </div>

              <div className="mt-9 grid gap-3 sm:flex sm:flex-wrap">
                <a
                  {...bookProps(l)}
                  data-testid={`location-book-${l.id}`}
                  className="sheen flex min-h-[56px] items-center justify-center gap-1.5 whitespace-nowrap bg-[#c5a059] px-7 font-cond text-[13px] font-bold uppercase tracking-[0.16em] text-[#0a0d12] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#d8b46b]"
                >
                  <span className="sheen-bar" />
                  Book {l.label} <ArrowUpRight size={13} />
                </a>
                <div className="grid grid-cols-2 gap-3 sm:flex">
                  <a
                    href={l.maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`location-directions-${l.id}`}
                    className="flex min-h-[56px] items-center justify-center whitespace-nowrap border border-white/15 px-7 font-cond text-[13px] font-bold uppercase tracking-[0.16em] text-slate-200 transition-colors duration-300 hover:border-[#c5a059] hover:text-[#d8b46b]"
                  >
                    Directions
                  </a>
                  <a
                    href={`tel:${l.tel}`}
                    data-testid={`location-call-btn-${l.id}`}
                    className="flex min-h-[56px] items-center justify-center whitespace-nowrap border border-white/15 px-7 font-cond text-[13px] font-bold uppercase tracking-[0.16em] text-slate-200 transition-colors duration-300 hover:border-[#c5a059] hover:text-[#d8b46b]"
                  >
                    Call
                  </a>
                </div>
              </div>

              <Link
                to={`/${l.id}`}
                data-testid={`location-page-link-${l.id}`}
                className="mt-5 inline-flex items-center gap-2 font-cond text-[11px] uppercase tracking-[0.2em] text-slate-500 underline decoration-[#c5a059]/40 underline-offset-4 transition-colors hover:text-[#d8b46b]"
              >
                More about the {l.label} shop <ArrowUpRight size={12} />
              </Link>

              <a
                href={l.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`location-ig-${l.id}`}
                className="mt-4 flex items-center gap-2 text-xs text-slate-400 transition-colors hover:text-[#d8b46b]"
              >
                <Instagram size={14} className="shrink-0 text-[#c5a059]" />
                {l.instagramHandle}
              </a>

            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div
          data-testid="community-perk-banner"
          className="mt-5 flex flex-col items-start justify-between gap-4 border border-[#c5a059]/25 bg-[#c5a059]/[0.07] p-6 sm:flex-row sm:items-center sm:p-7"
        >
          <p className="max-w-2xl text-sm leading-relaxed text-[#e6dcc6]">
            <span className="label mr-2">Local perk</span>
            Mention The Vault and get a{" "}
            <strong className="font-bold text-[#f3e9d3]">free 2-week trial</strong> at
            Gracie Barra Jiu-Jitsu right down the street.
          </p>
          <a
            href="sms:+19406005286"
            data-testid="perk-text-link"
            className="shrink-0 border border-[#c5a059]/50 px-5 py-3 font-cond text-[12px] font-bold uppercase tracking-[0.18em] text-[#e2cf9e] transition-colors hover:bg-[#c5a059] hover:text-[#0a0d12]"
          >
            Text 940-600-5286
          </a>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
